import { useEffect, useState } from "react";
import axios from "axios";
import { SkeletonList } from "./ReportSkeleton";
import { Users, BarChart3, Crown, Sparkles, ArrowLeft, Loader2, ShieldAlert, MessageCircle, DollarSign, TrendingUp, TrendingDown, Repeat, Mail, MailX, UserCheck, Percent, Gift, Save, Zap, PiggyBank, ShoppingCart, Clock3, Star, Star as StarIcon } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, AreaChart, Area } from "recharts";
import BrandingSettings from "./BrandingSettings";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const PLAN_BADGE = {
  free: "bg-gray-100 text-gray-700",
  pro: "bg-gradient-to-r from-yellow-400 to-orange-400 text-white",
  enterprise: "bg-gray-900 text-white",
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-3`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div className="text-2xl font-extrabold text-gray-900 mb-1 leading-tight">{value}</div>
    <div className="text-sm text-gray-500 leading-snug">{label}</div>
  </div>
);

export default function AdminPanel() {
  const { user, authLoading, login } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [refConfig, setRefConfig] = useState(null);
  const [refFlags, setRefFlags] = useState([]);
  const [intentData, setIntentData] = useState(null);
  const [reportFb, setReportFb] = useState(null);
  const [togglingPayments, setTogglingPayments] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const togglePayments = async () => {
    setTogglingPayments(true);
    try {
      const r = await axios.patch(`${API}/admin/payments-config`, { enabled: !intentData.payments_enabled });
      setIntentData((d) => ({ ...d, payments_enabled: r.data.enabled }));
    } catch {
      setError("No se pudo cambiar el estado de los pagos.");
    } finally {
      setTogglingPayments(false);
    }
  };
  const [configSaved, setConfigSaved] = useState(false);
  const [error, setError] = useState(null);
  const [planUpdating, setPlanUpdating] = useState(null);

  const saveRefConfig = async () => {
    setSavingConfig(true);
    try {
      const r = await axios.patch(`${API}/admin/config/referral`, {
        enabled: refConfig.enabled,
        reward_analyses: Number(refConfig.reward_analyses),
        max_referrals: Number(refConfig.max_referrals),
        blocked_email_domains: (typeof refConfig.blocked_email_domains === "string"
          ? refConfig.blocked_email_domains.split(/[\n,]+/)
          : refConfig.blocked_email_domains),
      });
      setRefConfig({ ...r.data.referral, blocked_email_domains: r.data.referral.blocked_email_domains.join("\n") });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2500);
    } catch {
      setError("No se pudo guardar la configuración de referidos.");
    } finally {
      setSavingConfig(false);
    }
  };

  const togglePlan = async (u) => {
    const newPlan = u.plan === "pro" ? "free" : "pro";
    setPlanUpdating(u.user_id);
    try {
      await axios.patch(`${API}/admin/users/${u.user_id}/plan`, { plan: newPlan });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, plan: newPlan } : x)));
      setStats((prev) =>
        prev
          ? {
              ...prev,
              free_users: prev.free_users + (newPlan === "free" ? 1 : -1),
              pro_users: prev.pro_users + (newPlan === "pro" ? 1 : -1),
            }
          : prev
      );
    } catch {
      setError("No se pudo cambiar el plan.");
    } finally {
      setPlanUpdating(null);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    Promise.all([
      axios.get(`${API}/admin/stats`),
      axios.get(`${API}/admin/users`),
      axios.get(`${API}/feedback`).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/email-logs`).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/config`).catch(() => ({ data: null })),
      axios.get(`${API}/admin/referral-flags`).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/purchase-intent`).catch(() => ({ data: null })),
      axios.get(`${API}/admin/report-feedback`).catch(() => ({ data: null })),
    ])
      .then(([s, u, f, e, c, rf, pi, rfb]) => {
        setStats(s.data);
        setUsers(u.data);
        setFeedback(f.data);
        setEmailLogs(e.data);
        if (c.data?.referral) {
          setRefConfig({ ...c.data.referral, blocked_email_domains: c.data.referral.blocked_email_domains.join("\n") });
        }
        setRefFlags(rf.data);
        setIntentData(pi.data);
        setReportFb(rfb.data);
      })
      .catch(() => setError("No se pudieron cargar los datos del panel."));
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
        <div data-testid="admin-access-denied" className="bg-white rounded-3xl shadow-xl p-10 text-center max-w-md">
          <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso restringido</h2>
          <p className="text-gray-600 mb-6">
            {user ? "Tu cuenta no tiene permisos de administrador." : "Este panel es solo para administradores. Inicia sesión con tu cuenta de Google autorizada."}
          </p>
          {!user && (
            <button
              data-testid="admin-login-button"
              onClick={login}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
            >
              Iniciar sesión con Google
            </button>
          )}
          <a href="/" className="block mt-4 text-sm text-purple-600 hover:underline">Volver a la app</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Panel de Administración
            </h1>
            <p className="text-sm text-gray-600">ViralTubeAI · {user.email}</p>
          </div>
          <a href="/" data-testid="admin-back-link" className="flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900">
            <ArrowLeft className="w-4 h-4" /> Volver a la app
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {error && <p className="text-red-600">{error}</p>}
        {!stats && !error && (
          <div data-testid="admin-section-loading"><SkeletonList rows={5} /></div>
        )}

        {stats && (
          <>
            {/* Stats */}
            <div data-testid="admin-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Usuarios registrados" value={stats.total_users} color="from-purple-500 to-pink-500" />
              <StatCard icon={BarChart3} label="Análisis realizados" value={stats.total_analyses} color="from-blue-500 to-cyan-500" />
              <StatCard icon={Sparkles} label="Usuarios gratuitos" value={stats.free_users} color="from-green-500 to-emerald-500" />
              <StatCard icon={Crown} label="Usuarios Pro" value={stats.pro_users} color="from-yellow-400 to-orange-500" />
            </div>

            {/* Negocio */}
            <div data-testid="admin-business-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={DollarSign} label="MRR" value={`${(stats.mrr ?? 0).toFixed(2)} $`} color="from-emerald-500 to-teal-500" />
              <StatCard icon={TrendingUp} label="ARR" value={`${(stats.arr ?? 0).toFixed(2)} $`} color="from-teal-500 to-cyan-600" />
              <StatCard icon={DollarSign} label="Ingresos 30 días" value={`${(stats.revenue_30d ?? 0).toFixed(2)} $`} color="from-indigo-500 to-purple-500" />
              <StatCard icon={DollarSign} label="Ingresos totales" value={`${(stats.total_revenue ?? 0).toFixed(2)} $`} color="from-purple-600 to-pink-600" />
              <StatCard icon={Percent} label="Conversión" value={`${stats.conversion_rate ?? 0}% (${stats.conversions ?? 0})`} color="from-pink-500 to-rose-500" />
              <StatCard icon={TrendingDown} label="Churn 30 días" value={`${stats.churn_rate ?? 0}%`} color="from-red-500 to-rose-600" />
              <StatCard icon={Repeat} label="Retención (repiten pago)" value={`${stats.retention_rate ?? 0}%`} color="from-amber-500 to-orange-500" />
              <StatCard icon={UserCheck} label="Activos últimos 7 días" value={stats.active_users_7d ?? 0} color="from-sky-500 to-blue-600" />
              <StatCard icon={Mail} label="Emails enviados" value={stats.emails_sent ?? 0} color="from-violet-500 to-purple-600" />
              <StatCard icon={MailX} label="Emails fallidos" value={stats.emails_failed ?? 0} color="from-gray-500 to-gray-700" />
              <StatCard icon={TrendingDown} label="Cancelaciones 30d" value={stats.cancellations_30d ?? 0} color="from-rose-400 to-red-500" />
              <StatCard icon={TrendingDown} label="Expiraciones 30d" value={stats.expirations_30d ?? 0} color="from-slate-400 to-slate-600" />
            </div>

            {/* Cache de análisis */}
            <div data-testid="admin-cache-stats" className="grid grid-cols-3 gap-4">
              <StatCard icon={Zap} label="Análisis desde cache" value={stats.cache_hits ?? 0} color="from-yellow-400 to-amber-500" />
              <StatCard icon={Percent} label="Tasa de cache" value={`${stats.cache_hit_rate ?? 0}%`} color="from-amber-500 to-orange-500" />
              <StatCard icon={PiggyBank} label="Ahorro de IA estimado" value={`${(stats.ai_savings ?? 0).toFixed(2)} $`} color="from-lime-500 to-green-600" />
            </div>

            {/* Ingresos por día */}
            {stats.revenue_by_day?.length > 0 && (
              <div data-testid="admin-revenue-chart" className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <h3 className="font-bold text-gray-800 mb-4">Ingresos por día (últimos 30 días)</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.revenue_by_day} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3e8ff" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <Tooltip formatter={(v) => [`${v} $`, "Ingresos"]} />
                      <Area type="monotone" dataKey="amount" name="Ingresos ($)" stroke="#10b981" fill="#d1fae5" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Growth chart */}
            {(stats.new_users_by_day?.length > 0 || stats.analyses_by_day?.length > 0) && (
              <div data-testid="admin-growth-chart" className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <h3 className="font-bold text-gray-800 mb-4">Crecimiento: usuarios y análisis por día</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={(() => {
                        const map = {};
                        (stats.new_users_by_day || []).forEach((d) => {
                          map[d.date] = { date: d.date, usuarios: d.count, análisis: 0 };
                        });
                        (stats.analyses_by_day || []).forEach((d) => {
                          map[d.date] = { ...(map[d.date] || { date: d.date, usuarios: 0 }), análisis: d.count };
                        });
                        return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
                      })()}
                      margin={{ top: 10, right: 20, left: -10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3e8ff" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="usuarios" name="Usuarios nuevos" fill="#8B5CF6" radius={[6, 6, 0, 0]} barSize={28} />
                      <Line type="monotone" dataKey="análisis" name="Análisis" stroke="#06B6D4" strokeWidth={3} dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}

        {/* Users table */}
        {users && (
          <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
            <h3 className="font-bold text-gray-800 px-6 pt-6 pb-2">Usuarios ({users.length})</h3>
            <div data-testid="admin-users-table" className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-purple-50 text-purple-700 text-xs uppercase">
                    <th className="px-4 py-3 text-left">Usuario</th>
                    <th className="px-4 py-3 text-left">Plan</th>
                    <th className="px-4 py-3 text-center">Análisis</th>
                    <th className="px-4 py-3 text-left">Registro</th>
                    <th className="px-4 py-3 text-left">Último acceso</th>
                    <th className="px-4 py-3 text-left">Última URL analizada</th>
                    <th className="px-4 py-3 text-left">IP / País / Dispositivo</th>
                    <th className="px-4 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-gray-100 hover:bg-purple-50/40">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{u.name || "—"}</div>
                        <div className="text-xs text-gray-500">{u.email}{u.is_admin && <span className="ml-2 text-purple-600 font-bold">ADMIN</span>}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${PLAN_BADGE[u.plan] || PLAN_BADGE.free}`}>{u.plan}</span>
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-gray-800">{u.analyses_used ?? 0}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(u.last_login)}</td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <span className="block truncate text-gray-600" title={u.last_analyzed_url || ""}>{u.last_analyzed_url || "—"}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {u.last_ip || "—"}<br />{u.last_country || "—"} · {u.last_device || "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.plan !== "enterprise" && (
                          <button
                            data-testid={`toggle-plan-${u.user_id}`}
                            onClick={() => togglePlan(u)}
                            disabled={planUpdating === u.user_id}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50 ${u.plan === "pro" ? "bg-gray-100 text-gray-700 hover:bg-gray-200" : "bg-gradient-to-r from-yellow-400 to-orange-400 text-white hover:shadow-md"}`}
                          >
                            {planUpdating === u.user_id ? "..." : u.plan === "pro" ? "Quitar Pro" : "Dar Pro"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Valoraciones de informes */}
        {reportFb && (
          <div data-testid="admin-report-feedback" className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <StarIcon className="w-5 h-5 text-yellow-500 fill-yellow-400" /> Valoraciones de informes
            </h3>
            <div className="flex flex-wrap items-center gap-6 mb-5">
              <div className="text-center">
                <p data-testid="report-fb-average" className="text-4xl font-extrabold text-gray-900">{reportFb.average || "—"}</p>
                <div className="flex justify-center my-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <StarIcon key={n} className={`w-4 h-4 ${reportFb.average >= n - 0.25 ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
                  ))}
                </div>
                <p className="text-xs text-gray-500">{reportFb.total} valoraciones</p>
              </div>
              <div className="flex-1 min-w-[220px] space-y-1">
                {[5, 4, 3, 2, 1].map((n) => {
                  const count = reportFb.distribution?.[String(n)] || 0;
                  const pct = reportFb.total ? (count / reportFb.total) * 100 : 0;
                  return (
                    <div key={n} className="flex items-center gap-2 text-xs">
                      <span className="w-8 text-gray-500 font-semibold">{n} ★</span>
                      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-6 text-gray-400 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {reportFb.items.length === 0 ? (
              <p className="text-xs text-gray-400">Aún no hay valoraciones.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {reportFb.items.map((it) => (
                  <div key={it.id} className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-600">{it.email}</span>
                      <span className="flex items-center gap-1 text-xs font-bold text-yellow-600">
                        {it.rating} <StarIcon className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                        <span className="text-gray-400 font-normal ml-2">{fmtDate(it.timestamp)}</span>
                      </span>
                    </div>
                    {it.comment && <p className="text-sm text-gray-700 mt-1.5">&ldquo;{it.comment}&rdquo;</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Intención de compra */}
        {intentData && (
          <div data-testid="admin-purchase-intent" className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-purple-600" /> Intención de compra
              </h3>
              <button
                data-testid="payments-toggle"
                onClick={togglePayments}
                disabled={togglingPayments}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-60 ${intentData.payments_enabled ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"}`}
              >
                {intentData.payments_enabled ? "✓ Pagos ACTIVADOS (Stripe)" : "⏳ Pagos en modo 'muy pronto' — clic para activar"}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-5">
              <StatCard icon={ShoppingCart} label="Intentos de compra" value={intentData.total_intents} color="from-purple-500 to-indigo-500" />
              <StatCard icon={Clock3} label="Lista de espera" value={intentData.waitlist_count} color="from-blue-500 to-cyan-500" />
              <StatCard icon={Star} label="Acceso anticipado" value={intentData.early_access_count} color="from-amber-400 to-orange-500" />
            </div>

            <h4 className="font-semibold text-gray-700 text-sm mb-2">Intentos de compra ({intentData.intents.length})</h4>
            {intentData.intents.length === 0 ? (
              <p className="text-xs text-gray-400 mb-4">Aún no hay intentos registrados.</p>
            ) : (
              <div data-testid="intent-table" className="overflow-x-auto max-h-72 overflow-y-auto mb-5">
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="bg-purple-50 text-purple-700 text-xs uppercase">
                      <th className="px-3 py-2.5 text-left">Fecha</th>
                      <th className="px-3 py-2.5 text-left">Usuario</th>
                      <th className="px-3 py-2.5 text-left">Plan</th>
                      <th className="px-3 py-2.5 text-left">País</th>
                      <th className="px-3 py-2.5 text-left">Origen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {intentData.intents.map((it) => (
                      <tr key={it.id} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{fmtDate(it.timestamp)}</td>
                        <td className="px-3 py-2 text-gray-700">{it.email}</td>
                        <td className="px-3 py-2 font-semibold text-gray-800">{it.plan_label}</td>
                        <td className="px-3 py-2 text-gray-600">{it.country || "—"}</td>
                        <td className="px-3 py-2 text-gray-500 max-w-[200px]"><span className="block truncate" title={it.origin}>{it.origin}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h4 className="font-semibold text-gray-700 text-sm mb-2">Lista de espera y acceso anticipado ({intentData.waitlist.length})</h4>
            {intentData.waitlist.length === 0 ? (
              <p className="text-xs text-gray-400">Nadie se ha apuntado todavía.</p>
            ) : (
              <div data-testid="waitlist-table" className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="bg-purple-50 text-purple-700 text-xs uppercase">
                      <th className="px-3 py-2.5 text-left">Fecha</th>
                      <th className="px-3 py-2.5 text-left">Email</th>
                      <th className="px-3 py-2.5 text-left">Plan</th>
                      <th className="px-3 py-2.5 text-left">País</th>
                      <th className="px-3 py-2.5 text-center">Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {intentData.waitlist.map((w) => (
                      <tr key={w.id} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{fmtDate(w.updated_at)}</td>
                        <td className="px-3 py-2 text-gray-700">{w.email}</td>
                        <td className="px-3 py-2 text-gray-600">{w.plan_label}</td>
                        <td className="px-3 py-2 text-gray-600">{w.country || "—"}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${w.type === "early_access" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                            {w.type === "early_access" ? "Acceso anticipado" : "Lista de espera"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Branding */}
        <BrandingSettings />

        {/* Configuración de referidos */}
        {refConfig && (
          <div data-testid="admin-referral-config" className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" /> Programa de referidos — configuración
            </h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <label className="text-sm">
                <span className="block text-xs font-bold text-gray-500 uppercase mb-1">Activado</span>
                <select
                  data-testid="ref-config-enabled"
                  value={refConfig.enabled ? "si" : "no"}
                  onChange={(e) => setRefConfig({ ...refConfig, enabled: e.target.value === "si" })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                >
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-xs font-bold text-gray-500 uppercase mb-1">Análisis por referido</span>
                <input
                  data-testid="ref-config-reward"
                  type="number" min="0" max="100"
                  value={refConfig.reward_analyses}
                  onChange={(e) => setRefConfig({ ...refConfig, reward_analyses: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs font-bold text-gray-500 uppercase mb-1">Máx. referidos por usuario</span>
                <input
                  data-testid="ref-config-max"
                  type="number" min="0" max="10000"
                  value={refConfig.max_referrals}
                  onChange={(e) => setRefConfig({ ...refConfig, max_referrals: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                />
              </label>
            </div>
            <label className="text-sm block mb-4">
              <span className="block text-xs font-bold text-gray-500 uppercase mb-1">Dominios de email bloqueados (uno por línea)</span>
              <textarea
                data-testid="ref-config-domains"
                rows={4}
                value={refConfig.blocked_email_domains}
                onChange={(e) => setRefConfig({ ...refConfig, blocked_email_domains: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-xs font-mono"
              />
            </label>
            <button
              data-testid="ref-config-save"
              onClick={saveRefConfig}
              disabled={savingConfig}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 transition-all"
            >
              <Save className="w-4 h-4" /> {savingConfig ? "Guardando..." : configSaved ? "✓ Guardado" : "Guardar configuración"}
            </button>

            {/* Casos sospechosos */}
            <div className="mt-6">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Casos sospechosos de auto-referido ({refFlags.length})</h4>
              {refFlags.length === 0 ? (
                <p className="text-xs text-gray-400">Sin casos sospechosos registrados.</p>
              ) : (
                <div data-testid="admin-referral-flags" className="space-y-2 max-h-60 overflow-y-auto">
                  {refFlags.map((f) => (
                    <div key={f.id} className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-xs">
                      <p className="font-semibold text-red-800">{f.referred_email} ← invitado por {f.referrer_email}</p>
                      <p className="text-red-600 mt-1">Señales: {f.reasons.join(" · ")}</p>
                      <p className="text-gray-400 mt-1">{fmtDate(f.timestamp)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email logs */}
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-600" /> Registro de emails ({emailLogs.length})
          </h3>
          {emailLogs.length === 0 ? (
            <p className="text-sm text-gray-500">Aún no hay emails registrados.</p>
          ) : (
            <div data-testid="admin-email-logs" className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="bg-purple-50 text-purple-700 text-xs uppercase">
                    <th className="px-4 py-3 text-left">Fecha</th>
                    <th className="px-4 py-3 text-left">Destinatario</th>
                    <th className="px-4 py-3 text-left">Asunto</th>
                    <th className="px-4 py-3 text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.map((log, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(log.timestamp)}</td>
                      <td className="px-4 py-2.5 text-gray-700">{log.email}</td>
                      <td className="px-4 py-2.5 text-gray-600 max-w-[280px]"><span className="block truncate" title={log.subject}>{log.subject}</span></td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${log.status === "sent" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                          title={log.error || ""}>
                          {log.status === "sent" ? "Enviado" : "Fallido"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Feedback */}
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-purple-600" /> Sugerencias de usuarios ({feedback.length})
          </h3>
          {feedback.length === 0 ? (
            <p className="text-sm text-gray-500">Aún no hay sugerencias.</p>
          ) : (
            <div data-testid="admin-feedback-list" className="space-y-3 max-h-96 overflow-y-auto">
              {feedback.map((item) => (
                <div key={item.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-gray-800 text-sm">{item.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{fmtDate(item.timestamp)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
